/**
 * ScheduledSendsQueuePage — Operate · Scheduled Sends Queue.
 *
 * Per audit deliverable 04 §3.S11. Future-dated sends across both pillars with
 * a live countdown, type badges, and per-row Cancel / Reschedule actions.
 *
 * Data source:
 *   - GET   /api/admin/operate/scheduled-sends            → { scheduled: [...] }
 *   - POST  /api/admin/operate/scheduled-sends/:id/cancel
 *   - PATCH /api/admin/operate/scheduled-sends/:id/reschedule { scheduled_at }
 *
 * Mirrors MeasureOverviewPage for fetch / loading / error conventions and is
 * wrapped in the shared PillarPageShell (operate = teal banner).
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Alert,
  Button,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PillarPageShell from "../shared/PillarPageShell";
import apiService from "../../../services/api";

const OPERATE_TEAL = "#146152";
const NAVY = "#1A5F7A";
const GOLD = "#D4AC0D";
const SLATE = "#5D6D7E";

interface ScheduledSend {
  id: string;
  name: string | null;
  subject_line: string | null;
  status: string | null;
  scheduled_at: string | null;
  total_recipients: number | null;
  campaign_type: string | null;
  sub_kind: string | null;
  created_at: string | null;
}

/** Tone for a campaign-type chip — marketing leans gold, transactional slate. */
const typeChipColor = (type?: string | null): string => {
  const t = (type ?? "").toLowerCase();
  if (t.includes("transac") || t.includes("trigger") || t.includes("system")) {
    return SLATE;
  }
  if (t.includes("market") || t.includes("nurture") || t.includes("campaign")) {
    return GOLD;
  }
  return NAVY;
};

/** Compute a human countdown ("2d 4h", "12m 30s", "Sending now") to a target. */
const formatCountdown = (
  iso: string | null | undefined,
  now: number,
): { label: string; overdue: boolean; soon: boolean } => {
  if (!iso) return { label: "—", overdue: false, soon: false };
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return { label: "—", overdue: false, soon: false };

  let diff = target - now;
  if (diff <= 0) return { label: "Sending now", overdue: true, soon: true };

  const soon = diff <= 60 * 60 * 1000; // within an hour

  const days = Math.floor(diff / 86_400_000);
  diff -= days * 86_400_000;
  const hours = Math.floor(diff / 3_600_000);
  diff -= hours * 3_600_000;
  const mins = Math.floor(diff / 60_000);
  diff -= mins * 60_000;
  const secs = Math.floor(diff / 1000);

  let label: string;
  if (days > 0) label = `${days}d ${hours}h`;
  else if (hours > 0) label = `${hours}h ${mins}m`;
  else if (mins > 0) label = `${mins}m ${secs}s`;
  else label = `${secs}s`;

  return { label, overdue: false, soon };
};

/** Absolute, locale-friendly timestamp for the tooltip / secondary line. */
const formatAbsolute = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/** Build the value for a <input type="datetime-local"> from an ISO string. */
const toDatetimeLocal = (iso: string | null | undefined): string => {
  const base = iso ? new Date(iso) : new Date();
  if (Number.isNaN(base.getTime())) return "";
  // Shift to local time so the control shows the wall-clock the admin expects.
  const off = base.getTimezoneOffset() * 60_000;
  return new Date(base.getTime() - off).toISOString().slice(0, 16);
};

const ScheduledSendsQueuePage: React.FC = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState<ScheduledSend[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Live "now" tick driving the countdowns — refreshed every 30s.
  const [now, setNow] = useState<number>(() => Date.now());

  // Per-row pending state so buttons disable while a request is in flight.
  const [pendingId, setPendingId] = useState<string | null>(null);

  // Reschedule dialog state.
  const [rescheduleTarget, setRescheduleTarget] = useState<ScheduledSend | null>(
    null,
  );
  const [rescheduleValue, setRescheduleValue] = useState<string>("");
  const [savingReschedule, setSavingReschedule] = useState(false);

  const fetchData = useCallback(async (signalMounted?: () => boolean) => {
    try {
      const res: any = await apiService.get(
        "/api/admin/operate/scheduled-sends",
      );
      if (signalMounted && !signalMounted()) return;
      // fetchWithAuth unwraps `data.data` when present; for `{ scheduled: [...] }`
      // the payload arrives as the whole object on `res.data`.
      const payload = res?.data ?? res;
      const list: ScheduledSend[] =
        payload?.scheduled ??
        payload?.data?.scheduled ??
        (Array.isArray(payload) ? payload : []);
      setRows(Array.isArray(list) ? list : []);
      setError(null);
    } catch (e: any) {
      if (signalMounted && !signalMounted()) return;
      setError(e?.message ?? "Failed to load scheduled sends");
    }
  }, []);

  // Initial load.
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      await fetchData(() => mounted);
      if (mounted) setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [fetchData]);

  // Countdown ticker — re-render every 30s without refetching.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const refresh = useCallback(async () => {
    await fetchData();
    setNow(Date.now());
  }, [fetchData]);

  const handleCancel = useCallback(
    async (row: ScheduledSend) => {
      const label = row.name || row.subject_line || "this send";
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        `Cancel "${label}"? This stops the scheduled send and cannot be undone.`,
      );
      if (!ok) return;

      setActionError(null);
      setPendingId(row.id);
      try {
        await apiService.post(
          `/api/admin/operate/scheduled-sends/${row.id}/cancel`,
        );
        await refresh();
      } catch (e: any) {
        setActionError(e?.message ?? "Failed to cancel send");
      } finally {
        setPendingId(null);
      }
    },
    [refresh],
  );

  const openReschedule = useCallback((row: ScheduledSend) => {
    setActionError(null);
    setRescheduleTarget(row);
    setRescheduleValue(toDatetimeLocal(row.scheduled_at));
  }, []);

  const closeReschedule = useCallback(() => {
    if (savingReschedule) return;
    setRescheduleTarget(null);
    setRescheduleValue("");
  }, [savingReschedule]);

  const submitReschedule = useCallback(async () => {
    if (!rescheduleTarget || !rescheduleValue) return;
    setActionError(null);
    setSavingReschedule(true);
    try {
      // Convert the local datetime back to an ISO instant for the API.
      const iso = new Date(rescheduleValue).toISOString();
      await apiService.patch(
        `/api/admin/operate/scheduled-sends/${rescheduleTarget.id}/reschedule`,
        { scheduled_at: iso },
      );
      setRescheduleTarget(null);
      setRescheduleValue("");
      await refresh();
    } catch (e: any) {
      setActionError(e?.message ?? "Failed to reschedule send");
    } finally {
      setSavingReschedule(false);
    }
  }, [rescheduleTarget, rescheduleValue, refresh]);

  // ── Derived KPI values (null-safe) ──────────────────────────────────────
  const total = rows?.length ?? null;
  const totalRecipients =
    rows != null
      ? rows.reduce((sum, r) => sum + (r.total_recipients ?? 0), 0)
      : null;
  const nextUp =
    rows && rows.length > 0
      ? [...rows]
          .filter((r) => r.scheduled_at)
          .sort(
            (a, b) =>
              new Date(a.scheduled_at as string).getTime() -
              new Date(b.scheduled_at as string).getTime(),
          )[0] ?? null
      : null;
  const nextUpCountdown = nextUp
    ? formatCountdown(nextUp.scheduled_at, now)
    : null;
  const dueSoon =
    rows != null
      ? rows.filter((r) => formatCountdown(r.scheduled_at, now).soon).length
      : null;

  const fmtNum = (n: number | null | undefined): string =>
    n != null ? n.toLocaleString() : "—";

  return (
    <PillarPageShell
      pillar="operate"
      title="Scheduled Sends Queue"
      subtitle="Future-dated sends across both pillars. Live countdown, cancel, and reschedule."
      rightActions={
        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            navigate("/dashboard/communications/marketing/compose")
          }
          sx={{
            borderColor: OPERATE_TEAL,
            color: OPERATE_TEAL,
            "&:hover": {
              borderColor: OPERATE_TEAL,
              bgcolor: "rgba(20,97,82,0.06)",
            },
          }}
        >
          Manage →
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {actionError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* ── KPI cards ──────────────────────────────────────────────────── */}
      {loading ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={104} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, borderLeft: `6px solid ${OPERATE_TEAL}` }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                  In Queue
                </Typography>
                <Typography variant="h5" sx={{ color: OPERATE_TEAL, mt: 1 }}>
                  {fmtNum(total)}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  scheduled send{total === 1 ? "" : "s"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                  Total Recipients
                </Typography>
                <Typography variant="h5" sx={{ color: NAVY, mt: 1 }}>
                  {fmtNum(totalRecipients)}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  across all queued sends
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                  Next Up
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: nextUpCountdown?.overdue ? GOLD : OPERATE_TEAL,
                    mt: 1,
                  }}
                >
                  {nextUpCountdown ? nextUpCountdown.label : "—"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {nextUp ? nextUp.name || nextUp.subject_line || "Untitled" : "Nothing queued"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                borderLeft: `6px solid ${dueSoon ? GOLD : OPERATE_TEAL}`,
              }}
            >
              <CardContent>
                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                  Due Within 1h
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: dueSoon ? GOLD : OPERATE_TEAL, mt: 1 }}
                >
                  {fmtNum(dueSoon)}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {dueSoon ? "Needs eyes on" : "All clear"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ── Queue table ───────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="overline" sx={{ color: "text.secondary" }}>
              Queue
            </Typography>
            <Button
              size="small"
              onClick={refresh}
              disabled={loading}
              sx={{ color: OPERATE_TEAL, textTransform: "none" }}
            >
              Refresh
            </Button>
          </Box>
        </CardContent>
        <Divider />

        {loading ? (
          <Box sx={{ p: 2 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={52} />
            ))}
          </Box>
        ) : rows && rows.length > 0 ? (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Name / Subject</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Recipients
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Sends In</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const cd = formatCountdown(row.scheduled_at, now);
                  const isPending = pendingId === row.id;
                  const chipColor = typeChipColor(row.campaign_type);
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "text.primary" }}
                        >
                          {row.name || "Untitled send"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {row.subject_line || "No subject"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {row.campaign_type ? (
                            <Chip
                              size="small"
                              label={row.campaign_type}
                              sx={{
                                bgcolor: `${chipColor}1A`,
                                color: chipColor,
                                fontWeight: 600,
                                borderRadius: 1,
                                textTransform: "capitalize",
                              }}
                            />
                          ) : (
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              —
                            </Typography>
                          )}
                          {row.sub_kind && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={row.sub_kind}
                              sx={{
                                borderColor: "rgba(0,0,0,0.18)",
                                color: "text.secondary",
                                borderRadius: 1,
                                textTransform: "capitalize",
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {row.total_recipients != null
                            ? row.total_recipients.toLocaleString()
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={formatAbsolute(row.scheduled_at)} arrow>
                          <Chip
                            size="small"
                            label={cd.label}
                            sx={{
                              fontWeight: 700,
                              borderRadius: 1,
                              bgcolor: cd.overdue
                                ? `${GOLD}1A`
                                : cd.soon
                                  ? `${GOLD}14`
                                  : `${OPERATE_TEAL}14`,
                              color: cd.overdue || cd.soon ? GOLD : OPERATE_TEAL,
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={isPending}
                            onClick={() => openReschedule(row)}
                            sx={{
                              textTransform: "none",
                              borderColor: NAVY,
                              color: NAVY,
                              "&:hover": { borderColor: NAVY, bgcolor: "rgba(26,95,122,0.06)" },
                            }}
                          >
                            Reschedule
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={isPending}
                            onClick={() => handleCancel(row)}
                            startIcon={
                              isPending ? <CircularProgress size={14} color="inherit" /> : undefined
                            }
                            sx={{ textTransform: "none" }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <Typography variant="h6" sx={{ color: "text.secondary" }}>
              No scheduled sends
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              Future-dated sends will appear here once you schedule a campaign.
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() =>
                navigate("/dashboard/communications/marketing/compose")
              }
              sx={{
                mt: 2,
                bgcolor: OPERATE_TEAL,
                textTransform: "none",
                "&:hover": { bgcolor: "#0f4a3e" },
              }}
            >
              Compose &amp; schedule a send
            </Button>
          </Box>
        )}
      </Card>

      {/* ── Reschedule dialog ─────────────────────────────────────────── */}
      <Dialog open={!!rescheduleTarget} onClose={closeReschedule} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>
          Reschedule send
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            {rescheduleTarget?.name ||
              rescheduleTarget?.subject_line ||
              "Untitled send"}
          </Typography>
          <TextField
            type="datetime-local"
            label="New send time"
            value={rescheduleValue}
            onChange={(e) => setRescheduleValue(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={savingReschedule}
          />
          {rescheduleTarget?.scheduled_at && (
            <Typography variant="caption" sx={{ color: "text.secondary", mt: 1, display: "block" }}>
              Currently scheduled for {formatAbsolute(rescheduleTarget.scheduled_at)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeReschedule} disabled={savingReschedule} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitReschedule}
            disabled={savingReschedule || !rescheduleValue}
            startIcon={
              savingReschedule ? <CircularProgress size={16} color="inherit" /> : undefined
            }
            sx={{
              bgcolor: OPERATE_TEAL,
              textTransform: "none",
              "&:hover": { bgcolor: "#0f4a3e" },
            }}
          >
            Save new time
          </Button>
        </DialogActions>
      </Dialog>
    </PillarPageShell>
  );
};

export default ScheduledSendsQueuePage;
